/**
 * Browser end of the live command bridge.
 *
 * Holds a WebSocket to server/bridge-server.js, dispatches incoming commands
 * against the command table, and replies with the result. Reconnects on its own
 * with backoff, so you can restart the bridge server (or start it after the tab
 * is already open) without touching the browser.
 *
 * The scene lives in the tab, which makes it vulnerable to anything that
 * reloads the page — most notably Android discarding a backgrounded tab. The
 * bridge holds the authoritative copy, so on connect this client reports the
 * revision it last saw and the bridge rebuilds it if it's behind.
 *
 * A fresh page reports `null` and always rebuilds. A page that merely lost its
 * socket reports the revision it already has, and if that still matches the
 * bridge, nothing happens at all — which is what keeps an already-connected tab
 * off the replay path.
 */

import { isMutating, REVISION_GUARDED } from './session-rules.js';
import { serializeScene } from './serialize-scene.js';
import { shouldRetryTicketFailure, ticketFailureKind } from './bridge-url.js';

const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 8000;
const DEFAULT_SERVER_COMMAND_TIMEOUT_MS = 10_000;
const CLOSE_REASON_MAX_CHARS = 280;
const CLOSE_REASON_SECRET = /ticket|jwt|assertion|bearer|webSocketUrl/i;
/** Application WebSocket text `ping` interval (not a protocol ping, not `{cmd:"ping"}`). */
export const HIBERNATION_KEEPALIVE_MS = 12_000;
/**
 * How long an application `ping` may go unanswered before the viewer treats
 * the socket as silently dead. iOS Safari can leave `readyState === OPEN`
 * after the connection has already died; a missing `pong` (and no other
 * WebSocket message) is the signal, not `readyState`.
 */
export const KEEPALIVE_PONG_WATCHDOG_MS = 1_500;
/** Close reason (and connection flag) that marks a watchdog-initiated close. */
export const KEEPALIVE_PONG_WATCHDOG_CLOSE_REASON = 'pong-watchdog';

function roundTiming(value) {
  return Math.round(Math.max(0, value) * 1000) / 1000;
}

function boundDiagnosticText(value) {
  if (typeof value !== 'string' || value.length === 0) return '';
  if (CLOSE_REASON_SECRET.test(value)) return '';
  return value.length > CLOSE_REASON_MAX_CHARS ? value.slice(0, CLOSE_REASON_MAX_CHARS) : value;
}

/**
 * A safe-to-display form of a bridge URL: scheme, host, and path only.
 *
 * A production URL carries a short-lived, single-use viewer ticket in its
 * query string; never show that, or any other query string, in the status
 * pill or the console.
 */
function redactBridgeUrl(rawUrl) {
  try {
    const parsed = new URL(rawUrl);
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    return 'bridge';
  }
}

/** The local Node bridge on the page's own hostname, used when nothing else is configured. */
function defaultLocalBridgeUrl() {
  const hostname = (typeof location !== 'undefined' && location.hostname) || 'localhost';
  return `ws://${hostname}:8787`;
}

/**
 * @param {object} options
 * @param {Record<string, Function>} options.commands  name -> handler(args)
 * @param {object} [options.registry]                  current registered scene
 * @param {(state: string, detail?: string, meta?: object) => void} [options.onStatus]
 * @param {(cmd: string, ok: boolean) => void} [options.onCommand]
 * @param {(detail: object) => void} [options.onHandshake] current socket authority confirmed without a restore
 * @param {(detail: object) => void} [options.onRestoreStart] authoritative replay starting
 * @param {(count: number, detail?: object) => void} [options.onRestore]  scene rebuilt from the log
 * @param {(pose: object) => void} [options.onCamera]    restore a saved viewpoint
 * @param {(history: object) => void} [options.onHistory] authoritative undo/redo state
 * @param {() => void} [options.reloadPage]              dispose a tainted browser context
 * @param {string} [options.url]                         fixed URL, used as-is for every connection generation
 * @param {() => Promise<string>} [options.resolveUrl]    acquires a fresh URL for every connection generation;
 *   takes priority over `options.url` when both are given
 */
export function connectBridge({
  commands,
  registry = null,
  onStatus = () => {},
  onCommand = () => {},
  onHandshake = () => {},
  onRestoreStart = () => {},
  onRestore = () => {},
  onCamera = () => {},
  onHistory = () => {},
  onStats = () => {},
  reloadPage = () => location.reload(),
  url,
  resolveUrl,
}) {
  let socket = null;
  let retryDelay = RECONNECT_MIN_MS;
  let closed = false;
  let reconnectTimer = null;
  let reconnectHalted = false;
  let connectionEpoch = 0;
  const activeMutatingEpochs = new Set();
  const awaitingCommitEpochs = new Map();
  const taintedEpochs = new Set();
  let reloadRequested = false;

  const getUrl = typeof resolveUrl === 'function'
    ? resolveUrl
    : () => Promise.resolve(url ?? defaultLocalBridgeUrl());

  // Never shown until a connection attempt resolves its actual URL. A
  // production URL carries a single-use ticket, so nothing about it is safe
  // to display before it has been redacted down to scheme/host/path.
  let displayUrl = typeof url === 'string' ? redactBridgeUrl(url) : 'bridge';

  /**
   * The bridge revision this page is at, or null if it has never been told.
   *
   * Survives reconnects (it's a closure variable) but not a reload — which is
   * exactly the distinction the bridge needs to tell "lost its socket" from
   * "started over".
   */
  let revision = null;

  /**
   * True while a rebuild is in flight.
   *
   * A rebuild clears the scene before replaying, so a live command landing in
   * the middle of one would act on a half-built scene — or worse, get wiped by
   * the clear it arrived just before.
   */
  const restoringEpochs = new Set();
  /**
   * Epochs that sent a fatal `restoreResult { ok: false }` — a restore rejection
   * that leaves this viewer unusable (the server will drop the socket).
   *
   * A recovered load-candidate rollback (`purpose === "load" && rolledBack`) is
   * not fatal: the server keeps the viewer connected. Those epochs stay out of
   * this set so later idle close, manual reconnect, and mutation taint behave
   * as they did before restore containment.
   */
  const failedRestoreEpochs = new Map();
  let appliedHistory = [];

  /** Last counts the bridge reported. The page can't know these on its own. */
  let stats = { revision: null, viewers: null, logged: null, persistence: null };

  /** Authoritative undo/redo state, mirrored from every revision broadcast. */
  let history = {
    canUndo: false,
    canRedo: false,
    undoLabel: null,
    redoLabel: null,
    undoDepth: 0,
    redoDepth: 0,
  };

  /**
   * Set right after a restore finishes applying locally. `onRestore` fires
   * with whatever `stats.revision` last said, which can still be the value
   * from *before* this restore — the broadcast that actually confirms the
   * restored revision as authoritative only arrives after the server
   * receives this viewer's acknowledgement. This flag lets the next
   * `revision` message complete that handshake once it does. Declared out
   * here (not inside `open()`) because both `handleRestore` and the message
   * handler need to read and set it, and `handleRestore` is a sibling of
   * `open()`, not nested inside it.
   */
  let pendingReadinessConfirmation = false;

  /** Resolver for an in-flight saveSession(), since the socket isn't request/response. */
  let pendingSession = null;
  const pendingLoads = new Map();
  let nextSessionLoadId = 1;

  const beforeCommandCallbacks = new Set();
  let nextControllerId = 1;
  let nextOperationId = 1;
  let serverCommandTimeoutMs = DEFAULT_SERVER_COMMAND_TIMEOUT_MS;
  const controllerPending = new Map();
  let nextLocalCommitId = 1;
  /** Local Execution / Cloud Sync v1 — requestId -> {resolve, reject, timer}. */
  const localCommitPending = new Map();

  let keepaliveTimer = null;
  let keepaliveConnection = null;
  let pongWatchdogTimer = null;
  let pongWatchdogConnection = null;
  const finishConnectionClose = new WeakMap();
  const visibilityTarget = globalThis.document;
  const pageShowTarget = globalThis.window ?? null;

  function cancelPongWatchdog(connection) {
    if (connection && pongWatchdogConnection !== connection) return;
    if (pongWatchdogTimer !== null) {
      clearTimeout(pongWatchdogTimer);
      pongWatchdogTimer = null;
    }
    pongWatchdogConnection = null;
  }

  function armPongWatchdog(connection) {
    cancelPongWatchdog();
    pongWatchdogConnection = connection;
    pongWatchdogTimer = setTimeout(() => {
      const watched = pongWatchdogConnection;
      pongWatchdogTimer = null;
      pongWatchdogConnection = null;
      // OPEN is not proof of life: Safari can report OPEN on a dead socket.
      if (closed || !watched || watched !== socket) return;
      if (watched.readyState !== WebSocket.OPEN) return;
      closeSilentSocket(watched);
    }, KEEPALIVE_PONG_WATCHDOG_MS);
    pongWatchdogTimer?.unref?.();
  }

  function isPongWatchdogClose(connection, event) {
    return connection?.__pongWatchdogClose === true
      || event?.reason === KEEPALIVE_PONG_WATCHDOG_CLOSE_REASON;
  }

  function closeSilentSocket(connection) {
    connection.__pongWatchdogClose = true;
    try {
      connection.close(1000, KEEPALIVE_PONG_WATCHDOG_CLOSE_REASON);
    } catch {
      // The transport is already unusable; recovery must not depend on close().
    }
    // Browser close() can remain CLOSING on a dead network. Retire this epoch
    // now through the same idempotent recovery path used by a real close event.
    finishConnectionClose.get(connection)?.({ reason: KEEPALIVE_PONG_WATCHDOG_CLOSE_REASON });
  }

  function sendKeepalivePing(connection = socket) {
    if (connection?.readyState !== WebSocket.OPEN) return;
    connection.send('ping');
    armPongWatchdog(connection);
  }

  function stopKeepalive(connection) {
    if (connection && keepaliveConnection !== connection) return;
    if (keepaliveTimer !== null) {
      clearInterval(keepaliveTimer);
      keepaliveTimer = null;
    }
    keepaliveConnection = null;
    cancelPongWatchdog(connection);
  }

  function startKeepalive(connection) {
    stopKeepalive();
    keepaliveConnection = connection;
    keepaliveTimer = setInterval(() => sendKeepalivePing(connection), HIBERNATION_KEEPALIVE_MS);
    // Node's test runner treats a live interval as a handle. Browsers return a
    // number here, so this is a no-op in the editor.
    keepaliveTimer?.unref?.();
  }

  function onVisibilityChange() {
    if (visibilityTarget?.visibilityState === 'visible') sendKeepalivePing();
  }

  function onPageShow() {
    sendKeepalivePing();
  }

  function onFreeze() {
    // Page Lifecycle freeze is not socket death. Cancel a pending watchdog so
    // a paused timer does not close() when the page resumes; do not close here.
    cancelPongWatchdog();
  }

  if (visibilityTarget && typeof visibilityTarget.addEventListener === 'function') {
    visibilityTarget.addEventListener('visibilitychange', onVisibilityChange);
    visibilityTarget.addEventListener('freeze', onFreeze);
  }
  if (pageShowTarget && typeof pageShowTarget.addEventListener === 'function') {
    pageShowTarget.addEventListener('pageshow', onPageShow);
  }

  function open() {
    if (closed) return;
    const epoch = ++connectionEpoch;
    onStatus('connecting', displayUrl, { generation: epoch });
    void beginConnection(epoch);
  }

  /**
   * Acquire a URL for this connection generation and open the socket.
   *
   * The acquisition is async (a production ticket fetch), so by the time it
   * resolves a newer `open()`/`reconnect()` call may already have moved past
   * this generation. The epoch check right after the await discards that
   * stale result instead of opening a second, obsolete socket — and means a
   * ticket fetched here is used exactly once, never cached for a later retry.
   */
  async function beginConnection(epoch) {
    let resolvedUrl;
    try {
      resolvedUrl = await getUrl();
    } catch (err) {
      if (epoch !== connectionEpoch || closed) return;
      console.warn(`[anvil] ${err?.message ?? 'could not resolve the bridge URL'}`);
      if (!shouldRetryTicketFailure(err)) {
        reconnectHalted = true;
        onStatus('disconnected', err?.message ?? 'bridge viewer recovery is paused', {
          generation: epoch,
          reconnect: false,
          ticketFailure: ticketFailureKind(err),
          storageBudget: err?.storageBudget ?? null,
        });
        return;
      }
      scheduleReconnect(epoch);
      return;
    }
    if (epoch !== connectionEpoch || closed) return;

    displayUrl = redactBridgeUrl(resolvedUrl);
    onStatus('connecting', displayUrl, { generation: epoch });

    const connection = new WebSocket(resolvedUrl);
    let connectionFinished = false;
    const execution = { tail: Promise.resolve() };
    let helloRevision = null;
    let handshakePending = true;
    // A confirmation pending on the previous connection is moot on a new one.
    pendingReadinessConfirmation = false;
    socket = connection;

    connection.addEventListener('open', () => {
      if (connectionFinished || closed || epoch !== connectionEpoch || socket !== connection) return;
      retryDelay = RECONNECT_MIN_MS;
      helloRevision = revision;
      handshakePending = true;
      startKeepalive(connection);
      onStatus('connected', displayUrl, { generation: epoch });
      console.info(`[anvil] bridge connected: ${displayUrl}`);
      send({ type: 'hello', revision });
    });

    connection.addEventListener('message', (event) => {
      if (connectionFinished || closed || epoch !== connectionEpoch || socket !== connection) return;
      cancelPongWatchdog(connection);
      if (event.data === 'pong' || event.data === 'ping') return;
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (message.type === 'command') {
        const receivedAt = performance.now();
        if (isMutating(message.cmd)) {
          void enqueueSceneWork(execution, () => handleCommand(message, connection, epoch, receivedAt));
        } else {
          void handleCommand(message, connection, epoch, receivedAt);
        }
      }
      else if (message.type === 'restore') {
        const receivedAt = performance.now();
        notifyBeforeCommand({ cmd: 'restore', args: { revision: message.revision } });
        onRestoreStart({ generation: epoch, revision: message.revision });
        void enqueueSceneWork(execution, () => handleRestore(message, connection, epoch, receivedAt));
      }
      else if (message.type === 'session') pendingSession?.(message);
      else if (message.type === 'sessionLoadResult') finishSessionLoad(message);
      else if (message.type === 'welcome') {
        if (Number.isFinite(message.timeout) && message.timeout > 0) {
          serverCommandTimeoutMs = message.timeout;
        }
      }
      else if (message.type === 'controllerAccepted') acceptController(message);
      else if (message.type === 'controllerResult') settleController(message);
      else if (message.type === 'localCommitResult') settleLocalCommit(message);
      else if (message.type === 'revision') {
        const previousRevision = revision;
        const equalHandshake = handshakePending && message.revision === helloRevision;
        handshakePending = false;
        if (!restoringEpochs.has(epoch)) {
          const awaiting = awaitingCommitEpochs.get(epoch);
          if (awaitingCommitEpochs.has(epoch)
              && Number.isInteger(message.revision)
              && (awaiting === null || message.revision > awaiting)) {
            awaitingCommitEpochs.delete(epoch);
          }
          revision = message.revision;
        }
        if (
          Number.isInteger(previousRevision)
          && Number.isInteger(message.revision)
          && message.revision > previousRevision
          && !restoringEpochs.has(epoch)
        ) {
          sendKeepalivePing(connection);
        }
        // stats.revision always mirrors the raw authoritative broadcast, even
        // mid-restore, so callers can compare "what I've applied" (revision)
        // against "what the bridge says is current" (stats.revision).
        stats = {
          revision: message.revision,
          viewers: message.viewers ?? null,
          logged: message.logged ?? null,
          persistence: message.persistence ?? null,
        };
        history = {
          canUndo: message.canUndo ?? history.canUndo,
          canRedo: message.canRedo ?? history.canRedo,
          undoLabel: 'undoLabel' in message ? message.undoLabel : history.undoLabel,
          redoLabel: 'redoLabel' in message ? message.redoLabel : history.redoLabel,
          undoDepth: message.undoDepth ?? history.undoDepth,
          redoDepth: message.redoDepth ?? history.redoDepth,
        };
        onStats(stats);
        onHistory(history);
        if (equalHandshake) {
          onHandshake({ generation: epoch, revision: message.revision, authorityRevision: message.revision });
        } else if (pendingReadinessConfirmation && !restoringEpochs.has(epoch) && revision === message.revision) {
          pendingReadinessConfirmation = false;
          onHandshake({ generation: epoch, revision: message.revision, authorityRevision: message.revision });
        }
      }
    });

    const finishClose = (event) => {
      if (connectionFinished) return;
      connectionFinished = true;
      stopKeepalive(connection);
      const watchdogClose = isPongWatchdogClose(connection, event);
      const restoring = restoringEpochs.has(epoch);
      const tainted = activeMutatingEpochs.has(epoch) || awaitingCommitEpochs.has(epoch)
        || taintedEpochs.has(epoch);
      const reportedFailure = failedRestoreEpochs.get(epoch);
      const closeCode = Number.isInteger(event?.code) ? event.code : null;
      const closeReason = boundDiagnosticText(event?.reason);
      console.warn('[anvil] bridge socket closed', {
        code: closeCode,
        ...(closeReason ? { reason: closeReason } : {}),
        restoring,
        tainted,
        ...(watchdogClose ? { pongWatchdog: true } : {}),
      });
      restoringEpochs.delete(epoch);
      if (socket === connection) {
        socket = null;
        rejectPendingLoads(new Error('disconnected before the scene load completed'));
        rejectControllerRequests('bridge connection closed');
        rejectLocalCommitRequests('bridge connection closed');
      }
      if (closed) return;
      // Mutation/commit taint is stronger than restore containment: an uncertain
      // local scene still needs the existing hard-reload, even if this epoch also
      // reported a restore failure.
      if (tainted) {
        revision = null;
        requestHardReload(epoch);
        return;
      }
      if (reportedFailure) {
        haltAfterReportedRestoreFailure(epoch, reportedFailure);
        return;
      }
      if (restoring) {
        revision = null;
        requestHardReload(epoch);
        return;
      }
      scheduleReconnect(epoch);
    };
    finishConnectionClose.set(connection, finishClose);
    connection.addEventListener('close', finishClose);

    // 'error' is always followed by 'close', so reconnection is handled there.
    // Swallow it here to avoid an unhandled-error console entry on every retry
    // while the bridge server is down.
    connection.addEventListener('error', () => {});
  }

  async function handleCommand({ id, cmd, args }, connection, epoch, receivedAt = performance.now()) {
    const startedAt = performance.now();
    const queueWaitMs = startedAt - receivedAt;
    let handlerMs = 0;
    let canonicalizeMs = 0;
    const timing = () => ({
      queueWaitMs: roundTiming(queueWaitMs),
      handlerMs: roundTiming(handlerMs),
      canonicalizeMs: roundTiming(canonicalizeMs),
      totalMs: roundTiming(performance.now() - receivedAt),
    });
    notifyBeforeCommand({ id, cmd, args: args ?? {} });

    if (isMutating(cmd) && (epoch !== connectionEpoch || connection !== socket)) {
      revision = null;
      return;
    }
    if (restoringEpochs.has(epoch)) {
      // Better a clear error the CLI can retry on than a command applied to a
      // scene that is mid-rebuild.
      replyTo(connection, {
        id,
        ok: false,
        error: 'viewer is rebuilding its scene — retry in a moment',
        timing: timing(),
      });
      return;
    }

    const handler = commands[cmd];

    if (typeof handler !== 'function') {
      // Reply with the valid names. A typo should correct itself on the next
      // attempt rather than looking like the bridge silently swallowed it.
      replyTo(connection, {
        id,
        ok: false,
        error: `unknown command '${cmd}'`,
        detail: { available: Object.keys(commands).sort() },
        timing: timing(),
      });
      onCommand(cmd, false, `unknown command '${cmd}'`);
      return;
    }

    try {
      if (isMutating(cmd)) {
        activeMutatingEpochs.add(epoch);
        awaitingCommitEpochs.set(epoch, revision);
      }
      const handlerStartedAt = performance.now();
      let result;
      try {
        result = await handler(args ?? {});
      } finally {
        handlerMs = performance.now() - handlerStartedAt;
      }

      // Some commands are better remembered as something else: a duplicate
      // records itself as an independent creation so the log never depends on a
      // source object that might later be deleted. That's protocol, not part of
      // the answer, so lift it out of the result rather than printing it.
      let record;
      let payload = result ?? null;
      if (Array.isArray(result?.record)) {
        ({ record, ...payload } = result);
      }

      if (epoch !== connectionEpoch || connection !== socket || connection.readyState !== WebSocket.OPEN) {
        if (isMutating(cmd)) {
          revision = null;
          requestHardReload(epoch);
        }
        return;
      }

      let canonical;
      if (isMutating(cmd)) {
        if (Array.isArray(record)) appliedHistory.push(...cloneHistory(record));
        else appliedHistory.push({ cmd, args: args ?? {}, ...(payload?.id ? { id: payload.id } : {}) });
        const canonicalStartedAt = performance.now();
        canonical = registry ? serializeScene(registry) : undefined;
        canonicalizeMs = performance.now() - canonicalStartedAt;
        if (canonical) appliedHistory = cloneHistory(canonical);
      }

      replyTo(connection, {
        id, ok: true, result: payload, mutating: isMutating(cmd), record,
        ...(canonical ? { canonical } : {}),
        timing: timing(),
      });
      // The bridge follows up with a `revision` message; we don't guess at the
      // new value here. Missing that message just leaves us stale, and a stale
      // viewer gets rebuilt on its next connect — which is correct either way.
      onCommand(cmd, true);
    } catch (err) {
      const message = err?.message ?? String(err);
      console.error(`[anvil] command '${cmd}' failed`, err);
      if (epoch === connectionEpoch && connection === socket) {
        replyTo(connection, {
          id,
          ok: false,
          error: message,
          timing: timing(),
        });
      }
      onCommand(cmd, false, message);
    } finally {
      if (isMutating(cmd)) taintIfSceneWorkSettledStale(connection, epoch);
      activeMutatingEpochs.delete(epoch);
    }
  }

  /**
   * Rebuild the scene to match the bridge.
   *
   * Always clears first, then replays the whole log. One code path covers both
   * cases the bridge sends this for: a fresh page is already empty so the clear
   * is a no-op, and a stale page needs exactly that rebuild. Trying to apply a
   * delta instead would need a stable index into the log, which compaction
   * takes away.
   *
   * Entries are applied directly rather than through handleCommand: these
   * aren't requests and nobody is waiting on a reply. Replay is fail-fast, and
   * a failed candidate is rolled back to the prior local history.
   */
  async function handleRestore(
    { restoreId, purpose = 'sync', revision: target = null, commands: history = [], camera },
    connection,
    epoch,
    receivedAt = performance.now(),
  ) {
    if (epoch !== connectionEpoch || connection !== socket) return;
    const startedAt = performance.now();
    const timing = () => ({
      queueWaitMs: roundTiming(startedAt - receivedAt),
      totalMs: roundTiming(performance.now() - receivedAt),
    });
    restoringEpochs.add(epoch);
    // A new restore starting supersedes whatever the previous one was waiting
    // to confirm.
    pendingReadinessConfirmation = false;
    const previousHistory = cloneHistory(appliedHistory);
    const previousRevision = revision;

    try {
      await replay(history, connection, epoch);
      assertCurrent(connection, epoch);
      const canonical = purpose === 'load' && registry ? serializeScene(registry) : undefined;
      revision = target;
      appliedHistory = cloneHistory(canonical ?? history);
      awaitingCommitEpochs.delete(epoch);
      console.info(`[anvil] rebuilt scene from ${history.length} command(s) at rev ${target}`);
      onRestore(commands.listObjects().count, { generation: epoch, revision: target, authorityRevision: stats.revision });
      if (camera) onCamera(camera);
      // stats.revision (above) may still be the value from before this
      // restore — the broadcast confirming `target` as authoritative only
      // arrives after the server receives the acknowledgement sent below.
      pendingReadinessConfirmation = true;
      sendTo(connection, {
        type: 'restoreResult', restoreId, ok: true, revision: target, rolledBack: false,
        ...(canonical ? { log: canonical } : {}),
        timing: timing(),
      });
    } catch (err) {
      if (err?.staleEpoch || epoch !== connectionEpoch || connection !== socket) {
        revision = null;
        requestHardReload(epoch);
        return;
      }
      const failedIndex = Number.isInteger(err?.failedIndex) ? err.failedIndex : undefined;
      let rolledBack = false;
      try {
        assertCurrent(connection, epoch);
        await replay(previousHistory, connection, epoch);
        assertCurrent(connection, epoch);
        revision = previousRevision;
        appliedHistory = previousHistory;
        awaitingCommitEpochs.delete(epoch);
        rolledBack = true;
        // A successful rollback also gets an eventual confirming broadcast
        // once the server sees this restoreResult (see the note above the
        // success path).
        pendingReadinessConfirmation = true;
      } catch (rollbackError) {
        revision = null;
        console.error('[anvil] restore rollback failed', rollbackError);
      }
      onRestore(commands.listObjects().count, { generation: epoch, revision: target, authorityRevision: stats.revision });
      const error = err?.message ?? String(err);
      console.warn(`[anvil] restore failed${failedIndex == null ? '' : ` at entry ${failedIndex}`}: ${error}`);
      if (connection.readyState === WebSocket.OPEN) {
        // Mark before send so a synchronous close inside send() still sees the
        // known failure. Recovered load rollback is not fatal: the server keeps
        // the viewer, so do not poison the epoch.
        if (!(purpose === 'load' && rolledBack === true)) {
          failedRestoreEpochs.set(epoch, { failedIndex, error });
        }
        sendTo(connection, {
          type: 'restoreResult', restoreId, ok: false, revision: target, rolledBack,
          ...(failedIndex == null ? {} : { failedIndex }), error, timing: timing(),
        });
      }
    } finally {
      taintIfSceneWorkSettledStale(connection, epoch);
      restoringEpochs.delete(epoch);
    }
  }

  function cloneHistory(history) {
    return history.map((entry) => ({ ...entry, args: { ...(entry.args ?? {}) } }));
  }

  async function replay(history, connection, epoch) {
    assertCurrent(connection, epoch);
    await commands.clearScene();
    for (let index = 0; index < history.length; index++) {
      assertCurrent(connection, epoch);
      const entry = history[index];
      const handler = commands[entry?.cmd];
      if (typeof handler !== 'function') {
        const error = new Error(`unknown command '${entry?.cmd}'`);
        error.failedIndex = index;
        throw error;
      }
      try {
        await handler(entry.id ? { ...entry.args, id: entry.id } : (entry.args ?? {}));
        assertCurrent(connection, epoch);
      } catch (cause) {
        if (cause?.staleEpoch) throw cause;
        const error = new Error(cause?.message ?? String(cause));
        error.failedIndex = index;
        throw error;
      }
    }
  }

  function assertCurrent(connection, epoch) {
    if (epoch === connectionEpoch && connection === socket && connection.readyState === WebSocket.OPEN) return;
    const error = new Error('obsolete connection generation');
    error.staleEpoch = true;
    throw error;
  }

  /** Back off and try the next connection generation, unless we've been told to stop. */
  function scheduleReconnect(epoch) {
    if (closed || reconnectHalted) return;
    onStatus('disconnected', `retrying in ${Math.round(retryDelay / 100) / 10}s`, { generation: epoch });
    reconnectTimer = setTimeout(open, retryDelay);
    retryDelay = Math.min(retryDelay * 2, RECONNECT_MAX_MS);
  }

  function requestHardReload(epoch) {
    taintedEpochs.add(epoch);
    if (reloadRequested || closed || reconnectHalted) return;
    reloadRequested = true;
    reloadPage();
  }

  function haltAfterReportedRestoreFailure(epoch, failure) {
    reconnectHalted = true;
    const bounded = boundDiagnosticText(failure.error);
    const atEntry = Number.isInteger(failure.failedIndex) ? ` at entry ${failure.failedIndex}` : '';
    const detail = `restore failed${atEntry}${bounded ? `: ${bounded}` : ''}`;
    onStatus('disconnected', detail, {
      generation: epoch,
      reconnect: false,
    });
  }

  function taintIfSceneWorkSettledStale(connection, epoch) {
    if (failedRestoreEpochs.has(epoch)) return;
    if (epoch === connectionEpoch && connection === socket && connection.readyState === WebSocket.OPEN) return;
    revision = null;
    requestHardReload(epoch);
  }

  function enqueueSceneWork(execution, work) {
    const operation = execution.tail.catch(() => {}).then(work);
    execution.tail = operation.catch(() => {});
    return operation;
  }

  function send(payload) {
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
  }

  function sendTo(connection, payload) {
    if (connection?.readyState === WebSocket.OPEN) connection.send(JSON.stringify(payload));
  }

  /** Report the viewpoint so it can be handed back after a reload. */
  function reportCamera(pose) {
    send({ type: 'camera', camera: pose });
  }

  function reply(payload) {
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'reply', ...payload }));
    }
  }

  function replyTo(connection, payload) {
    sendTo(connection, { type: 'reply', ...payload });
  }

  function disconnect() {
    closed = true;
    stopKeepalive();
    if (visibilityTarget && typeof visibilityTarget.removeEventListener === 'function') {
      visibilityTarget.removeEventListener('visibilitychange', onVisibilityChange);
      visibilityTarget.removeEventListener('freeze', onFreeze);
    }
    if (pageShowTarget && typeof pageShowTarget.removeEventListener === 'function') {
      pageShowTarget.removeEventListener('pageshow', onPageShow);
    }
    clearTimeout(reconnectTimer);
    socket?.close();
  }

  /**
   * Drop the socket and let the usual backoff bring it back.
   *
   * Useful for shaking out a connection that's gone weird, and it's how the
   * verification suite produces a stale viewer without reloading the page.
   */
  function reconnect() {
    retryDelay = RECONNECT_MIN_MS;
    reconnectHalted = false;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (socket) {
      socket.close();
      return;
    }
    if (!closed) open();
  }

  /**
   * Ask the bridge for the whole session so it can be saved to a file.
   *
   * The socket has no request/response shape, so this parks a resolver for the
   * one `session` message that comes back. One at a time is plenty — nobody
   * clicks Save twice in the same 5 seconds, and a timeout beats hanging.
   */
  function requestSession(timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected to the bridge'));
        return;
      }
      const timer = setTimeout(() => {
        pendingSession = null;
        reject(new Error('the bridge did not send the session in time'));
      }, timeoutMs);

      pendingSession = (message) => {
        clearTimeout(timer);
        pendingSession = null;
        resolve(message);
      };
      send({ type: 'sessionRequest' });
    });
  }

  function finishSessionLoad(message) {
    const pending = pendingLoads.get(message.id);
    if (!pending) return;
    pendingLoads.delete(message.id);
    if (message.ok) pending.resolve(message);
    else {
      const error = new Error(message.error ?? 'scene load failed');
      if (Number.isInteger(message.failedIndex)) error.failedIndex = message.failedIndex;
      pending.reject(error);
    }
  }

  function rejectPendingLoads(error) {
    for (const pending of pendingLoads.values()) {
      pending.reject(error);
    }
    pendingLoads.clear();
  }

  /** Replace the bridge's session after acknowledged replay and persistence. */
  function loadSession(session) {
    return new Promise((resolve, reject) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected to the bridge'));
        return;
      }
      const id = String(nextSessionLoadId++);
      pendingLoads.set(id, { resolve, reject });
      send({
        type: 'sessionLoad', id,
        ...(session?.version === undefined ? {} : { version: session.version }),
        log: session?.log,
        camera: session?.camera ?? null,
      });
    });
  }

  function createOperationId() {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return `browser:${globalThis.crypto.randomUUID()}`;
    }
    return `browser:${Date.now().toString(36)}:${nextOperationId++}:${Math.random().toString(36).slice(2)}`;
  }

  function unknownOutcomeError(message, pending) {
    const error = new Error(`${message}; outcome unknown — retry with operation id '${pending.operationId}'`);
    error.unknownOutcome = true;
    error.accepted = pending.accepted;
    error.operationId = pending.operationId;
    return error;
  }

  /**
   * Queue one idempotent browser-controller operation.
   *
   * The server acknowledges before the operation waits on its mutation queue.
   * That acknowledgement clears the browser deadline: only the server's own
   * per-command deadline applies once execution begins, so a long queue cannot
   * make the browser report a false failure. If the socket dies, callers get
   * the stable operation id and can retry without applying the operation twice.
   */
  function controllerMessage(type, payload, { operationId = createOperationId() } = {}) {
    return new Promise((resolve, reject) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected to the bridge'));
        return;
      }
      const requestId = String(nextControllerId++);
      const pending = {
        resolve,
        reject,
        timer: null,
        operationId,
        accepted: false,
      };
      const timer = setTimeout(() => {
        controllerPending.delete(requestId);
        reject(unknownOutcomeError(
          `bridge did not acknowledge the operation within ${serverCommandTimeoutMs}ms`,
          pending,
        ));
      }, serverCommandTimeoutMs);
      pending.timer = timer;
      controllerPending.set(requestId, pending);
      send({ type, requestId, ...payload, operationId });
    });
  }

  function acceptController(message) {
    const pending = controllerPending.get(message.requestId);
    if (!pending) return;
    if (message.operationId !== pending.operationId) {
      controllerPending.delete(message.requestId);
      clearTimeout(pending.timer);
      pending.reject(unknownOutcomeError('bridge acknowledged a different operation id', pending));
      return;
    }
    pending.accepted = true;
    clearTimeout(pending.timer);
    pending.timer = null;
  }

  function settleController(message) {
    const pending = controllerPending.get(message.requestId);
    if (!pending) return;
    if (message.operationId && message.operationId !== pending.operationId) return;
    controllerPending.delete(message.requestId);
    clearTimeout(pending.timer);
    if (message.ok) pending.resolve(message.result);
    else {
      const error = new Error(message.error ?? 'bridge controller request failed');
      if ('result' in message) error.result = message.result;
      error.operationId = pending.operationId;
      error.unknownOutcome = false;
      pending.reject(error);
    }
  }

  function rejectControllerRequests(message) {
    for (const [requestId, pending] of controllerPending) {
      clearTimeout(pending.timer);
      controllerPending.delete(requestId);
      pending.reject(unknownOutcomeError(message, pending));
    }
  }

  /**
   * Local Execution / Cloud Sync v1 — submit an ALREADY-EXECUTED local
   * mutation for durable commit. Deliberately not built on `controllerMessage`:
   * that path relays a command for the bridge to execute; this path only ever
   * carries a mutation this page's own `src/commands.js` instance has already
   * applied, plus its resulting canonical scene, and the bridge must not
   * relay or re-execute it — see `#handleLocalCommit` in
   * cloud/bridge/project-bridge.ts and `handleLocalCommit` in
   * server/bridge-server.js. Resolves with the server's `localCommitResult`
   * message on any definitive answer (`ok: true` or `ok: false`); rejects
   * only when the outcome is unknown (timeout or disconnect before an
   * answer arrives) — the caller (`src/local-execution.js`) is what decides
   * whether an unknown outcome means "retry with the same operationId" or
   * "reconcile from authoritative state".
   */
  function submitLocalCommit(payload, { timeoutMs = serverCommandTimeoutMs } = {}) {
    return new Promise((resolve, reject) => {
      if (socket?.readyState !== WebSocket.OPEN) {
        reject(new Error('not connected to the bridge'));
        return;
      }
      const requestId = String(nextLocalCommitId++);
      const pending = { resolve, reject, timer: null, operationId: payload.operationId };
      const timer = setTimeout(() => {
        localCommitPending.delete(requestId);
        reject(unknownOutcomeError(
          `bridge did not acknowledge the local commit within ${timeoutMs}ms`,
          pending,
        ));
      }, timeoutMs);
      pending.timer = timer;
      localCommitPending.set(requestId, pending);
      send({ type: 'localCommit', requestId, ...payload });
    });
  }

  function settleLocalCommit(message) {
    const pending = localCommitPending.get(message.requestId);
    if (!pending) return;
    if (message.operationId && message.operationId !== pending.operationId) return;
    localCommitPending.delete(message.requestId);
    clearTimeout(pending.timer);
    pending.resolve(message);
  }

  function rejectLocalCommitRequests(message) {
    for (const [requestId, pending] of localCommitPending) {
      clearTimeout(pending.timer);
      localCommitPending.delete(requestId);
      pending.reject(unknownOutcomeError(message, pending));
    }
  }

  /**
   * Revision-guarded commands need a `baseRevision`, and the bridge validates
   * it before relaying. A UI action has, by definition, been issued against the
   * revision this page is currently rendering, so the page supplies it here
   * rather than making every call site remember to.
   *
   * Without this, every guarded command was simply unreachable from the UI:
   * the bridge rejected it with "requires a numeric baseRevision" before it
   * ever reached a viewer. That is a parity failure — the capability existed
   * for agents and not for humans — and it is why this belongs at the one
   * choke point every UI command already passes through instead of being
   * sprinkled across panels.
   */
  function execute(cmd, args = {}, { label, operationId } = {}) {
    const guarded = REVISION_GUARDED.has(cmd) && args.baseRevision === undefined && typeof revision === 'number'
      ? { ...args, baseRevision: revision }
      : args;
    return controllerMessage('controllerRequest', { cmd, args: guarded, label }, { operationId });
  }

  function notifyBeforeCommand(command) {
    for (const callback of beforeCommandCallbacks) callback(command);
  }

  function beforeCommand(callback) {
    if (typeof callback !== 'function') throw new TypeError('beforeCommand callback must be a function');
    beforeCommandCallbacks.add(callback);
    return () => beforeCommandCallbacks.delete(callback);
  }

  /**
   * Execute one authoritative history step. Failed batches reject with an
   * Error whose `result` holds the per-entry outcomes and partial counts.
   */
  function executeBatch(entries, { label, continueOnError = false, operationId } = {}) {
    return controllerMessage(
      'controllerBatch',
      { entries, label, continue: continueOnError },
      { operationId },
    );
  }

  /**
   * Local Execution / Cloud Sync v1 — reconcile from cloud-authoritative
   * state after a revision mismatch or a local-commit failure. Deliberately
   * reuses the existing reconnect → hello(null) → full restore path (the
   * same one an ordinary dropped connection already goes through) instead of
   * inventing a second "fetch and rebuild" mechanism: forcing `revision` to
   * `null` before reconnecting guarantees the next hello mismatches, so the
   * bridge sends a full authoritative rebuild regardless of what this page's
   * own (possibly wrong, post-mismatch) revision bookkeeping thinks.
   */
  function forceReconcile() {
    revision = null;
    reconnect();
  }

  /**
   * Local Execution / Cloud Sync v1 — record the revision a successful
   * local-commit advanced to, so this connection's own hello/reconnect
   * bookkeeping (and the `revision` getter CLI/UI code already reads) stays
   * correct without a round trip through the gated command/relay path.
   */
  function noteCommittedRevision(nextRevision) {
    if (Number.isInteger(nextRevision)) revision = nextRevision;
  }

  function undo() {
    return execute('undo');
  }

  function redo() {
    return execute('redo');
  }

  open();
  return {
    disconnect,
    reconnect,
    reportCamera,
    requestSession,
    loadSession,
    execute,
    executeBatch,
    submitLocalCommit,
    forceReconcile,
    noteCommittedRevision,
    beforeCommand,
    undo,
    redo,
    get url() { return displayUrl; },
    get revision() { return revision; },
    get stats() { return stats; },
    get history() { return { ...history }; },
    /**
     * Local Execution / Cloud Sync v1 — "connected AND authoritatively
     * ready" in one place: an open socket, a known revision (at least one
     * completed handshake on this connection), and no restore in flight for
     * the current connection epoch. `src/local-execution.js` gates local-hot
     * execution on this before touching the scene.
     */
    get ready() {
      return socket?.readyState === WebSocket.OPEN
        && revision !== null
        && !restoringEpochs.has(connectionEpoch);
    },
  };
}
